import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { DownloadIcon } from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import Button from "../../Button";
import { PremiumTag } from "../../PremiumFeatureModal";
import { formatMoney } from "../../../../Utils";
import FormError from "../../Form/FormError";

// Every figure in the drawer sits on one right edge in tabular figures, so the
// columns of a payslip add up by eye.
const Amount = ({ value, className }) => (
  <span className={clsx("tabular-nums whitespace-nowrap", className)}>
    {formatMoney(value)}
  </span>
);

const Line = ({ label, value, note, emphasis = false, muted = false }) => (
  <div
    className={clsx(
      "flex items-baseline justify-between gap-4 py-1.5",
      emphasis && "border-t border-gray-200 mt-1 pt-2.5",
    )}
  >
    <dt
      className={clsx(
        "text-sm min-w-0",
        emphasis ? "font-semibold text-gray-900" : "text-gray-600",
      )}
    >
      {label}
      {note && <span className="block text-xs text-gray-500">{note}</span>}
    </dt>
    <dd
      className={clsx(
        "text-sm text-right",
        emphasis ? "font-semibold text-gray-900" : muted ? "text-gray-500" : "text-gray-900",
      )}
    >
      {value}
    </dd>
  </div>
);

const Section = ({ title, children, footnote }) => (
  <section className="py-4 border-b border-gray-200 last:border-b-0">
    <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
    <dl>{children}</dl>
    {footnote}
  </section>
);

const formatRate = (rate, locale) =>
  new Intl.NumberFormat(locale === "en" ? "en-US" : "id-ID", {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(rate || 0);

/**
 * The payslip view of one employee's month: income down to gross, the tax on
 * it, what comes off, and take-home pay - in the order a payslip is read.
 * Employer JHT/JP close it out because they are owed but are not the
 * employee's income.
 *
 * `onDownload` prints the same payslip as a PDF (premium); it is offered once
 * the run is posted, since a draft's figures can still change.
 */
const PayrollRunItemDetail = ({
  open,
  onClose,
  item,
  monthLabel,
  formatMonths,
  onDownload,
  downloading = false,
  downloadError = null,
  isFree = false,
}) => {
  const { t, i18n } = useTranslation();

  if (!item) return null;

  const isGrossUp = item.taxMethod === "GROSS_UP";
  const deductions = item.deductions || [];
  const taxableAllowances = (item.allowances || []).filter((line) => line.isTaxable);
  // Paid in cash but outside bruto, so they join on the way to take-home pay.
  const untaxedAllowances = (item.allowances || []).filter((line) => !line.isTaxable);
  const employerPremiums =
    (item.employerHealth || 0) + (item.employerJkk || 0) + (item.employerJkm || 0);
  const projected = item.ytdProjectedMonths || [];

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={`${item.employeeName || t("payroll.detail.title")} · ${monthLabel}`}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          {onDownload ? (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={DownloadIcon}
              onClick={() => onDownload(item)}
              loading={downloading}
            >
              {t("payroll.payslip.download")}
              {isFree && <PremiumTag />}
            </Button>
          ) : (
            <span />
          )}
          <Link
            to={`/employee/salary/${item.employeeID}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          >
            {t("payroll.actions.salaryTable")}
          </Link>
          <FormError className="basis-full" error={downloadError} />
        </div>
      }
    >
      <div className="text-sm text-gray-600 flex flex-wrap gap-x-4 gap-y-1 pb-3 border-b border-gray-200">
        {item.employeeCode && <span>{item.employeeCode}</span>}
        <span>
          {t("payroll.detail.fields.ptkp")} {item.ptkpStatus}
        </span>
        <span>
          {t("payroll.detail.fields.terCategory")} {item.terCategory}
        </span>
        <span>{t(`payroll.detail.taxMethods.${item.taxMethod || "GROSS"}`)}</span>
      </div>

      <Section title={t("payroll.detail.sections.income")}>
        <Line label={t("payroll.detail.fields.baseSalary")} value={<Amount value={item.baseSalary} />} />
        {taxableAllowances.map((line, index) => (
          <Line key={line.sourceID || index} label={line.name} value={<Amount value={line.amount} />} />
        ))}
        {item.attendanceDeduction > 0 && (
          <Line
            label={t("payroll.detail.fields.attendanceDeduction")}
            note={item.isAttendanceDeductionCapped ? t("payroll.detail.fields.attendanceCapped") : null}
            value={<Amount value={-item.attendanceDeduction} />}
          />
        )}
        {item.employerHealth > 0 && (
          <Line
            label={t("payroll.detail.fields.employerHealth")}
            value={<Amount value={item.employerHealth} />}
          />
        )}
        {item.employerJkk > 0 && (
          <Line label={t("payroll.detail.fields.employerJkk")} value={<Amount value={item.employerJkk} />} />
        )}
        {item.employerJkm > 0 && (
          <Line label={t("payroll.detail.fields.employerJkm")} value={<Amount value={item.employerJkm} />} />
        )}
        {isGrossUp && (
          <Line label={t("payroll.detail.fields.taxAllowance")} value={<Amount value={item.taxAllowance} />} />
        )}
        <Line emphasis label={t("payroll.detail.fields.gross")} value={<Amount value={item.grossSalary} />} />
      </Section>

      <Section
        title={t("payroll.detail.sections.tax")}
        footnote={
          projected.length > 0 && (
            <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              {t("payroll.detail.projectedNote", { months: formatMonths(projected) })}
            </p>
          )
        }
      >
        {item.isLastTaxPeriod ? (
          <>
            <Line
              label={t("payroll.detail.fields.yearlyTaxableIncome")}
              value={<Amount value={item.yearlyTaxableIncome} />}
            />
            <Line label={t("payroll.detail.fields.yearlyTax")} value={<Amount value={item.yearlyTax} />} />
          </>
        ) : (
          // A TER month is rate x bruto and nothing else; biaya jabatan and
          // pension only enter at the last tax period.
          <Line
            label={t("payroll.detail.fields.terRate")}
            value={<span className="tabular-nums">{formatRate(item.terRate, i18n.language)}</span>}
          />
        )}
        <Line
          emphasis
          label={t(item.isLastTaxPeriod ? "payroll.detail.fields.pph21LastPeriod" : "payroll.detail.fields.pph21")}
          note={item.pph21 < 0 ? t("payroll.detail.fields.refund") : null}
          value={<Amount value={item.pph21} />}
        />
      </Section>

      {/* From bruto to take-home: the premiums were never cash to the
          employee, untaxed allowances were, and deductions and the tax come
          off. The lines reconcile to netPay. */}
      <Section title={t("payroll.detail.sections.netPay")}>
        <Line label={t("payroll.detail.fields.gross")} value={<Amount value={item.grossSalary} />} />
        {employerPremiums > 0 && (
          <Line
            label={t("payroll.detail.fields.employerPremiums")}
            value={<Amount value={-employerPremiums} />}
          />
        )}
        {untaxedAllowances.map((line, index) => (
          <Line
            key={line.sourceID || index}
            label={line.name}
            note={t("payroll.detail.fields.notTaxable")}
            value={<Amount value={line.amount} />}
          />
        ))}
        {deductions.map((line, index) => (
          <Line key={line.sourceID || index} label={line.name} value={<Amount value={-line.amount} />} />
        ))}
        {item.pph21 !== 0 && (
          <Line
            label={t(item.pph21 < 0 ? "payroll.detail.fields.pph21Refund" : "payroll.detail.fields.pph21Withheld")}
            value={<Amount value={-item.pph21} />}
          />
        )}
        <Line emphasis label={t("payroll.detail.fields.netPay")} value={<Amount value={item.netPay} />} />
      </Section>

      {(item.employerJht > 0 || item.employerJp > 0) && (
        <Section title={t("payroll.detail.sections.employer")}>
          {item.employerJht > 0 && (
            <Line label={t("payroll.detail.fields.employerJht")} value={<Amount value={item.employerJht} />} />
          )}
          {item.employerJp > 0 && (
            <Line label={t("payroll.detail.fields.employerJp")} value={<Amount value={item.employerJp} />} />
          )}
        </Section>
      )}
    </SlideOver>
  );
};

export default PayrollRunItemDetail;
